import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Link,
  Unlink,
  Download,
  Search,
  Loader2,
  Check,
  ArrowRight,
} from 'lucide-react';

export default function PayrollEmployeeMapping({ api, platform, platformName, connectionId }) {
  const [loading, setLoading] = useState(false);
  const [externalEmployees, setExternalEmployees] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [linking, setLinking] = useState(null);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const connQ = connectionId ? `?connection_id=${connectionId}` : '';
      const res = await api.get(`/payroll/${platform}/employees${connQ}`);
      setExternalEmployees(res.data.employees || []);
      setWorkers(res.data.workers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to fetch ${platformName} employees`);
    } finally {
      setLoading(false);
    }
  };

  const idField = platform === 'xero' ? 'xero_employee_id' : 'myob_employee_uid';
  // Per-connection: the backend annotates each worker with linked_external_id for
  // the target connection; fall back to the legacy flat field otherwise.
  const linkedId = (w) => (connectionId ? w.linked_external_id : (w.linked_external_id ?? w[idField]));

  const unmappedWorkers = useMemo(() => {
    return workers.filter(w => !linkedId(w));
  }, [workers, connectionId]);

  const unmappedExternal = useMemo(() => {
    return externalEmployees.filter(e => !e.is_mapped && e.status !== 'Inactive');
  }, [externalEmployees]);

  const filteredExternal = useMemo(() => {
    if (!search.trim()) return externalEmployees;
    const q = search.toLowerCase();
    return externalEmployees.filter(e =>
      (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q)
    );
  }, [externalEmployees, search]);

  // Auto-match suggestions: match by name (case-insensitive)
  const autoMatches = useMemo(() => {
    const matches = [];
    for (const ext of unmappedExternal) {
      const nameMatch = unmappedWorkers.find(w =>
        w.name && ext.name && w.name.toLowerCase().trim() === ext.name.toLowerCase().trim()
      );
      if (nameMatch) {
        matches.push({ worker: nameMatch, external: ext });
      }
    }
    return matches;
  }, [unmappedWorkers, unmappedExternal]);

  const handleLink = async (workerId, externalId, externalName, payFrequency) => {
    setLinking(workerId);
    try {
      await api.post('/payroll/link-employee', {
        worker_id: workerId,
        platform,
        connection_id: connectionId || null,
        external_employee_id: externalId,
        external_employee_name: externalName,
        pay_frequency: payFrequency || null,
      });
      toast.success(`Linked successfully`);
      await fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    } finally {
      setLinking(null);
    }
  };

  const handleUnlink = async (workerId) => {
    setLinking(workerId);
    try {
      const connQ = connectionId ? `&connection_id=${connectionId}` : '';
      await api.post(`/payroll/unlink-employee?worker_id=${workerId}&platform=${platform}${connQ}`);
      toast.success('Unlinked');
      await fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    } finally {
      setLinking(null);
    }
  };

  const handleImportAll = async () => {
    const toImport = unmappedExternal.filter(e => !autoMatches.some(m => m.external.external_id === e.external_id));
    if (toImport.length === 0) {
      toast.info('No unmatched employees to import');
      return;
    }
    setImporting(true);
    try {
      const res = await api.post('/payroll/import-employees', {
        platform,
        connection_id: connectionId || null,
        employees: toImport.map(e => ({
          external_id: e.external_id,
          name: e.name,
          email: e.email,
          phone: e.phone,
          pay_frequency: e.pay_frequency || null,
        })),
      });
      toast.success(`Created ${res.data.created_count} workers, skipped ${res.data.skipped_count}`);
      await fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleLinkAllMatches = async () => {
    for (const match of autoMatches) {
      await handleLink(match.worker.id, match.external.external_id, match.external.name, match.external.pay_frequency);
    }
  };

  // Mapped workers list
  const mappedWorkers = useMemo(() => {
    return workers.filter(w => linkedId(w));
  }, [workers, connectionId]);

  return (
    <Card className="card-industrial">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          {platformName} Employee Mapping
        </CardTitle>
        <CardDescription>Link Core workers to {platformName} employees for payroll export</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fetch button */}
        {externalEmployees.length === 0 && (
          <Button onClick={fetchEmployees} disabled={loading} variant="outline" data-testid={`fetch-${platform}-employees`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Fetch {platformName} Employees
          </Button>
        )}

        {loading && <div className="text-sm text-muted-foreground">Loading employees from {platformName}...</div>}

        {externalEmployees.length > 0 && (
          <>
            {/* Summary */}
            <div className="flex gap-3 flex-wrap">
              <Badge variant="outline">{externalEmployees.length} {platformName} employees</Badge>
              <Badge variant="outline">{workers.length} Core workers</Badge>
              <Badge variant="outline" className="text-green-400 border-green-400/30">{mappedWorkers.length} linked</Badge>
              <Badge variant="outline" className="text-amber-400 border-amber-400/30">{unmappedWorkers.length} unlinked</Badge>
              {autoMatches.length > 0 && (
                <Badge variant="outline" className="text-blue-400 border-blue-400/30">{autoMatches.length} auto-matches</Badge>
              )}
            </div>

            {/* Auto-matches */}
            {autoMatches.length > 0 && (
              <div className="p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-400">Auto-matched by name ({autoMatches.length})</span>
                  <Button size="sm" onClick={handleLinkAllMatches} disabled={!!linking} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`link-all-${platform}`}>
                    <Link className="h-3 w-3 mr-1" /> Link All
                  </Button>
                </div>
                {autoMatches.map(m => (
                  <div key={m.worker.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{m.worker.name}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span>{m.external.name} ({m.external.email || 'no email'})</span>
                  </div>
                ))}
              </div>
            )}

            {/* Linked workers */}
            {mappedWorkers.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-medium text-muted-foreground">Linked Workers</h4>
                {mappedWorkers.map(w => {
                  const ext = externalEmployees.find(e => e.external_id === linkedId(w));
                  return (
                    <div key={w.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/20 text-sm">
                      <div className="flex items-center gap-2">
                        <Check className="h-3.5 w-3.5 text-green-400" />
                        <span>{w.name}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">{ext?.name || (linkedId(w) || '').substring(0, 8) + '...'}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleUnlink(w.id)} disabled={linking === w.id}>
                        <Unlink className="h-3 w-3 mr-1" /> Unlink
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Manual linking — search external employees */}
            {unmappedWorkers.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Manual Linking</h4>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${platformName} employees...`}
                    className="pl-8 h-9"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1 border rounded-md p-2">
                  {filteredExternal.filter(e => !e.is_mapped).map(ext => (
                    <div key={ext.external_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/20 text-sm">
                      <div>
                        <span className="font-medium">{ext.name}</span>
                        {ext.email && <span className="text-muted-foreground ml-2 text-xs">{ext.email}</span>}
                      </div>
                      <Select onValueChange={(workerId) => handleLink(workerId, ext.external_id, ext.name, ext.pay_frequency)}>
                        <SelectTrigger className="w-[180px] h-7 text-xs">
                          <SelectValue placeholder="Link to worker..." />
                        </SelectTrigger>
                        <SelectContent>
                          {unmappedWorkers.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {filteredExternal.filter(e => !e.is_mapped).length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-4">All employees are mapped</div>
                  )}
                </div>
              </div>
            )}

            {/* Import remaining unmatched */}
            {unmappedExternal.length > 0 && (
              <div className="flex items-center gap-3 pt-2">
                <Button onClick={handleImportAll} disabled={importing} variant="outline" size="sm" data-testid={`import-${platform}-employees`}>
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Import {unmappedExternal.length} Unmatched as New Workers
                </Button>
                <span className="text-xs text-muted-foreground">Creates Core worker records for employees not yet linked</span>
              </div>
            )}

            {/* Refresh */}
            <Button onClick={fetchEmployees} disabled={loading} variant="ghost" size="sm" className="text-xs">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Refresh
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
